import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { FiCheckSquare, FiClock, FiCalendar, FiPlus, FiPlay, FiPause, FiAlertCircle, FiFolder } from 'react-icons/fi';
import { dashboardService, taskService } from '../services/api';
import { useNotification } from '../context/NotificationContext';
import { timeTrackingApi } from '../services/timeTrackingApi';
import { useTimeTracking } from '../context/TimeTrackingContext';
import ManualTimeEntry from '../components/ManualTimeEntry';
import api from '../services/api';

const EmployeeDashboard = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [stats, setStats] = useState({
    assignedTasks: 0,
    completedTasks: 0,
    pendingTasks: 0,
    upcomingDeadlines: 0
  });
  
  const [myTasks, setMyTasks] = useState([]);
  const [upcomingProjectTasks, setUpcomingProjectTasks] = useState([]);
  const [showLeaveForm, setShowLeaveForm] = useState(false);
  const [leaveRequests, setLeaveRequests] = useState([]);
  const [leaveForm, setLeaveForm] = useState({
    start_date: '',
    end_date: '',
    reason: ''
  });
  const [localSelectedProject, setLocalSelectedProject] = useState('');
  const [localSelectedTask, setLocalSelectedTask] = useState('');
  const [projects, setProjects] = useState([]);
  const [projectTasks, setProjectTasks] = useState([]);
  const [screenshots, setScreenshots] = useState([]);
  const [todayTotalTime, setTodayTotalTime] = useState(0);
  const [totalManualSeconds, setTotalManualSeconds] = useState(0);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  
  const { sessionId, isTracking, selectedProject, elapsedTime, startTracking, stopTracking, projectName, taskTitle } = useTimeTracking();
  
  const { testNotification } = useNotification();
  
  // Refresh today's total time every 30 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      fetchTodayTotalTime();
    }, 30000);
    return () => clearInterval(interval);
  }, [isTracking, sessionId]);
  
  const takeScreenshot = async () => {
    if (window.electronAPI) {
      try {
        const screenshot = await window.electronAPI.takeScreenshot();
        if (screenshot && sessionId) {
          await timeTrackingApi.addScreenshot(sessionId, screenshot);
          setScreenshots(prev => [...prev, { path: screenshot, time: new Date() }]);
        }
      } catch (error) {
        console.error('Failed to take screenshot:', error);
      }
    }
  };
  
  const handleStartTracking = async () => {
    if (!window.electronAPI) {
      alert('Time tracking is only available in the desktop app. Please download and use the desktop version.');
      return;
    }
    
    if (!localSelectedProject || !localSelectedTask) {
      console.log('Missing project or task selection');
      return;
    }
    
    try {
      console.log('Starting tracking for project:', localSelectedProject, 'task:', localSelectedTask);
      await startTracking(parseInt(localSelectedProject), parseInt(localSelectedTask));
      setScreenshots([]);
      takeScreenshot(); // Take initial screenshot
    } catch (error) {
      console.error('Failed to start tracking:', error);
    }
  };
  
  const handleStopTracking = async () => {
    try {
      await stopTracking();
      fetchTodayTotalTime(); // Refresh today's total
    } catch (error) {
      console.error('Failed to stop tracking:', error);
    }
  };
  
  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        setLoading(true);
        
        // Fetch employee stats
        const statsResponse = await dashboardService.getEmployeeStats();
        const statsData = statsResponse.data;
        
        // Calculate stats from API data
        const totalTasks = statsData.totalTasks || 0;
        const completedTasks = statsData.tasksByStatus?.find(s => s._id === 'completed')?.count || 0;
        const pendingTasks = totalTasks - completedTasks;
        
        // Get upcoming deadlines (tasks due in the next 7 days)
        let upcomingDeadlines = 0;
        try {
          const tasksResponse = await taskService.getTasks({ 
            dueDate: { $gte: new Date(), $lte: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000) }
          });
          
          if (tasksResponse.data) {
            upcomingDeadlines = tasksResponse.data.filter(task => 
              task.status !== 'completed' && new Date(task.dueDate) <= new Date(Date.now() + 7 * 24 * 60 * 60 * 1000)
            ).length;
          }
        } catch (err) {
          console.warn('Could not fetch upcoming deadlines:', err);
          // Continue with the dashboard even if this specific call fails
        }
        
        // This is now handled in the try-catch block above
        
        setStats({
          assignedTasks: totalTasks,
          completedTasks,
          pendingTasks,
          upcomingDeadlines
        });
        
        setMyTasks(statsData.recentTasks || []);
        fetchProjects();
        fetchUpcomingProjectTasks();
        
        // Fetch today's total time
        fetchTodayTotalTime();
        
        // Fetch leave requests
        fetchLeaveRequests();
        
        setLoading(false);
      } catch (err) {
        console.error('Error fetching dashboard data:', err);
        setError('Failed to load dashboard data');
        setLoading(false);
      }
    };
    
    fetchDashboardData();
    
    // Notification is now handled globally
  }, []);
  
  // Reset timer when component unmounts
  useEffect(() => {
    return () => {
      if (isTracking && sessionId) {
        timeTrackingApi.stopTracking(sessionId);
      }
    };
  }, []);
  
  const fetchUpcomingProjectTasks = async () => {
    try {
      const currentUser = JSON.parse(localStorage.getItem('user') || '{}');
      const projectsRes = await api.post('/projects/list', { limit: 100 });
      const projects = projectsRes.data.projects || [];
      const results = await Promise.all(
        projects.map(async (project) => {
          try {
            const res = await api.post(`/projects/${project.id}/tasks`);
            const allTasks = Array.isArray(res.data) ? res.data : (res.data.tasks || []);
            const myTasks = allTasks.filter(t =>
              t.status !== 'completed' &&
              t.Users && t.Users.some(u => u.id === currentUser.id)
            );
            return myTasks.length > 0 ? { project, tasks: myTasks } : null;
          } catch { return null; }
        })
      );
      setUpcomingProjectTasks(results.filter(Boolean));
    } catch (err) {
      console.error('Error fetching upcoming project tasks:', err);
    }
  };

  const fetchProjects = async () => {
    try {
      const response = await api.get('/projects');
      setProjects(response.data.projects || response.data || []);
    } catch (error) {
      console.error('Error fetching projects:', error);
    }
  };
  
  const fetchProjectTasks = async (projectId) => {
    try {
      const response = await api.get(`/tasks?project_id=${projectId}`);
      console.log('Project tasks API response:', response.data);
      
      let tasks = [];
      if (response.data) {
        // Handle both paginated and direct array responses
        if (Array.isArray(response.data)) {
          tasks = response.data;
        } else if (response.data.tasks && Array.isArray(response.data.tasks)) {
          tasks = response.data.tasks;
        } else if (response.data.data && Array.isArray(response.data.data)) {
          tasks = response.data.data;
        }
      }
      
      console.log('Setting project tasks:', tasks);
      setProjectTasks(tasks);
    } catch (error) {
      console.error('Error fetching project tasks:', error);
      setProjectTasks([]); // Ensure it's always an array
    }
  };
  
  const fetchTodayTotalTime = async () => {
    try {
      console.log('Fetching today total time...');
      const response = await timeTrackingApi.getTodayTotalTime();
      console.log('Today total time response:', response.data);
      setTodayTotalTime(response.data.total_seconds || 0);
      setTotalManualSeconds(response.data.manual_seconds || 0);
    } catch (error) {
      console.error('Error fetching today total time:', error);
      setTodayTotalTime(0);
      setTotalManualSeconds(0);
    }
  };
  
  const fetchLeaveRequests = async () => {
    try {
      const response = await api.get('/leave-requests');
      setLeaveRequests(response.data);
    } catch (error) {
      console.error('Error fetching leave requests:', error);
    }
  };
  
  const handleLeaveSubmit = async (e) => {
    e.preventDefault();
    try {
      await api.post('/leave-requests', leaveForm);
      setLeaveForm({ start_date: '', end_date: '', reason: '' });
      setShowLeaveForm(false);
      fetchLeaveRequests();
      alert('Leave request submitted successfully!');
    } catch (error) {
      console.error('Error submitting leave request:', error);
      alert('Failed to submit leave request');
    }
  };
  
  const getStatusBadge = (status) => {
    const colors = {
      pending: 'bg-yellow-100 text-yellow-800',
      approved: 'bg-green-100 text-green-800',
      rejected: 'bg-red-100 text-red-800'
    };
    return colors[status] || colors.pending;
  };
  
  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high': return 'bg-danger';
      case 'medium': return 'bg-warning';
      case 'low': return 'bg-success';
      default: return 'bg-secondary-400';
    }
  };
  
  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return 'bg-success';
      case 'in_progress': return 'bg-warning';
      case 'todo': return 'bg-secondary-400';
      default: return 'bg-secondary-400';
    }
  };
  
  const formatDate = (dateString) => {
    if (!dateString) return 'No due date';
    const date = new Date(dateString);
    return date.toLocaleDateString();
  };
  
  return (
    <div className="p-6">
      {/* Notification is now global */}
      
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-semibold text-secondary-900">My Dashboard</h1>
        <button
          onClick={testNotification}
          className="px-3 py-1 bg-blue-500 text-white rounded text-sm hover:bg-blue-600"
        >
          Test Notification
        </button>
      </div>
      
      {loading ? (
        <div className="text-center py-8">
          <p className="text-secondary-500">Loading dashboard data...</p>
        </div>
      ) : error ? (
        <div className="text-center py-8">
          <p className="text-danger">{error}</p>
          <button 
            onClick={() => window.location.reload()} 
            className="mt-2 px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700"
          >
            Retry
          </button>
        </div>
      ) : (
        <>
          {/* Stats */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
            <div className="bg-white rounded-lg shadow-card p-6">
              <div className="flex items-center">
                <div className="p-3 rounded-full bg-primary-100 text-primary-600">
                  <FiCheckSquare size={24} />
                </div>
                <div className="ml-4">
                  <h2 className="text-sm font-medium text-secondary-500">My Tasks</h2>
                  <p className="text-2xl font-semibold text-secondary-900">{stats.assignedTasks}</p>
                  <div className="mt-1 text-xs">
                    <span className="text-success">{stats.completedTasks} completed</span>
                    {' • '}
                    <span className="text-secondary-500">{stats.pendingTasks} pending</span>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="bg-white rounded-lg shadow-card p-6">
              <div className="flex items-center">
                <div className="p-3 rounded-full bg-primary-100 text-primary-600">
                  <FiClock size={24} />
                </div>
                <div className="ml-4">
                  <h2 className="text-sm font-medium text-secondary-500">Upcoming Deadlines</h2>
                  <p className="text-2xl font-semibold text-secondary-900">{stats.upcomingDeadlines}</p>
                  <div className="mt-1 text-xs text-secondary-500">
                    In the next 7 days
                  </div>
                </div>
              </div>
            </div>
            
            <div className="bg-white rounded-lg shadow-card p-6">
              <div className="flex items-center">
                <div className="p-3 rounded-full bg-primary-100 text-primary-600">
                  <FiCalendar size={24} />
                </div>
                <div className="ml-4">
                  <h2 className="text-sm font-medium text-secondary-500">Completion Rate</h2>
                  <p className="text-2xl font-semibold text-secondary-900">
                    {stats.assignedTasks > 0 ? Math.round((stats.completedTasks / stats.assignedTasks) * 100) : 0}%
                  </p>
                  <div className="mt-1 text-xs text-secondary-500">
                    Overall
                  </div>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
      
      {false && !loading && !error && (
        /* Time Tracking Widget - Hidden */
        <div className="bg-white rounded-lg shadow-card p-6 mb-8">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-lg font-medium text-secondary-900">Time Tracking</h2>
            <div className="text-right">
              <div className="text-sm text-gray-500">Today's Total</div>
              <div className="text-xl font-mono font-bold text-blue-600">
                {Math.floor(todayTotalTime / 3600).toString().padStart(2, '0')}:
                {Math.floor((todayTotalTime % 3600) / 60).toString().padStart(2, '0')}:
                {(todayTotalTime % 60).toString().padStart(2, '0')}
              </div>
              <div className="text-xs text-gray-500 mt-1">
                <span className="text-blue-600">Auto: {Math.floor((todayTotalTime - (totalManualSeconds || 0)) / 3600)}h {Math.floor(((todayTotalTime - (totalManualSeconds || 0)) % 3600) / 60)}m</span>
                {totalManualSeconds > 0 && (
                  <><span className="mx-1">•</span>
                  <span className="text-green-600">Manual: {Math.floor(totalManualSeconds / 3600)}h {Math.floor((totalManualSeconds % 3600) / 60)}m</span></>
                )}
              </div>
            </div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
            {/* Project Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Select Project</label>
              <select
                value={localSelectedProject}
                onChange={(e) => {
                  setLocalSelectedProject(e.target.value);
                  setLocalSelectedTask('');
                  if (e.target.value) {
                    fetchProjectTasks(e.target.value);
                  } else {
                    setProjectTasks([]);
                  }
                }}
                className="w-full border border-gray-300 rounded-md px-3 py-2"
                disabled={isTracking}
              >
                <option value="">Choose a project...</option>
                {projects.map(project => (
                  <option key={project.id} value={project.id}>
                    {project.name}
                  </option>
                ))}
              </select>
            </div>
            
            {/* Task Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Select Task</label>
              <select
                value={localSelectedTask}
                onChange={(e) => setLocalSelectedTask(e.target.value)}
                className="w-full border border-gray-300 rounded-md px-3 py-2"
                disabled={isTracking || !localSelectedProject}
              >
                <option value="">Choose a task...</option>
                {Array.isArray(projectTasks) && projectTasks.map(task => (
                  <option key={task.id} value={task.id}>
                    {task.title}
                  </option>
                ))}
              </select>
            </div>
            
            {/* Timer Display */}
            <div className="flex items-center justify-between">
              <div>
                <div className="text-2xl font-mono font-bold text-gray-900">
                  {Math.floor(elapsedTime / 3600).toString().padStart(2, '0')}:
                  {Math.floor((elapsedTime % 3600) / 60).toString().padStart(2, '0')}:
                  {(elapsedTime % 60).toString().padStart(2, '0')}
                </div>
                <div className="text-sm text-gray-500">
                  {isTracking ? 'Tracking...' : 'Stopped'}
                </div>
              </div>
              
              <button
                onClick={isTracking ? handleStopTracking : handleStartTracking}
                disabled={(!localSelectedProject || !localSelectedTask) && !isTracking}
                className={`px-4 py-2 rounded-md flex items-center ${
                  isTracking 
                    ? 'bg-red-500 hover:bg-red-600 text-white' 
                    : 'bg-green-500 hover:bg-green-600 text-white disabled:bg-gray-300'
                }`}
                title={!window.electronAPI ? 'Time tracking only available in desktop app' : ''}
              >
                {isTracking ? (
                  <><FiPause className="mr-2" size={16} /> Stop</>
                ) : (
                  <><FiPlay className="mr-2" size={16} /> {window.electronAPI ? 'Start' : 'Desktop Only'}</>
                )}
              </button>
            </div>
          </div>
          
          {isTracking && (
            <div className="mt-4 p-3 bg-green-50 border border-green-200 rounded-md">
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <FiClock className="text-green-600 mr-2" />
                  <span className="text-sm text-green-800">
                    Tracking: {projectName} - {taskTitle}
                  </span>
                </div>
                <div className="text-xs text-green-600">
                  Screenshots: {screenshots.length}
                </div>
              </div>
            </div>
          )}
        </div>
      )}
      
      {false && !loading && !error && (
        /* Manual Time Entry Widget */
        <div className="bg-white rounded-lg shadow-card p-6 mb-8">
          <h2 className="text-lg font-medium text-secondary-900 mb-4">Add Manual Time</h2>
          
          <ManualTimeEntry onTimeAdded={() => {
            fetchTodayTotalTime();
            setRefreshTrigger(prev => prev + 1);
          }} />
        </div>
      )}
      
      {!loading && !error && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          {/* Upcoming Tasks */}
          <div className="bg-white rounded-lg shadow-card p-6">
            <div className="flex items-center gap-3 mb-5">
              <div className="p-2 rounded-lg bg-indigo-100 text-indigo-600">
                <FiClock size={20} />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-gray-900">Upcoming Tasks</h2>
                <p className="text-xs text-gray-400">{upcomingProjectTasks.flatMap(p => p.tasks).length} active tasks assigned to you</p>
              </div>
            </div>
            {upcomingProjectTasks.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-10 text-gray-400">
                <FiCheckSquare size={36} className="mb-2 opacity-30" />
                <p className="text-sm">No upcoming tasks assigned to you.</p>
              </div>
            ) : (
              <div className="space-y-2">
                {upcomingProjectTasks.flatMap(({ project, tasks }) =>
                  tasks.map(task => {
                    const isOverdue = task.due_date && new Date(task.due_date) < new Date();
                    const dueDateLabel = task.due_date
                      ? new Date(task.due_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
                      : 'No due date';
                    const statusColor =
                      task.status === 'in_progress' ? 'bg-blue-100 text-blue-700' :
                      task.status === 'testing' ? 'bg-purple-100 text-purple-700' :
                      task.status === 'client_review' ? 'bg-orange-100 text-orange-700' :
                      'bg-gray-100 text-gray-600';
                    const priorityColor =
                      task.priority === 'high' ? 'bg-red-500 text-white' :
                      task.priority === 'medium' ? 'bg-yellow-100 text-yellow-700' :
                      'bg-green-100 text-green-700';
                    const rowBorder = task.priority === 'high' ? 'border-l-4 border-red-400' : 'border-l-4 border-transparent';
                    return (
                      <div key={task.id} className={`flex items-center justify-between px-3 py-2 rounded-lg bg-gray-50 hover:bg-gray-100 transition ${rowBorder}`}>
                        <div className="flex-1 min-w-0 mr-3">
                          <Link to={`/employee/tasks/${task.id}`} className="text-sm font-medium text-gray-900 hover:text-indigo-600 truncate block">
                            {task.title}
                          </Link>
                          <Link to={`/employee/tasks`} className="text-xs text-gray-400 hover:text-indigo-500 flex items-center gap-1">
                            <FiFolder size={10} /> {project.name}
                          </Link>
                        </div>
                        <div className="flex items-center gap-1.5 flex-shrink-0">
                          <span className={`px-1.5 py-0.5 text-xs rounded-full font-medium ${statusColor}`}>
                            {(task.status || '').replace(/_/g, ' ')}
                          </span>
                          <span className={`px-1.5 py-0.5 text-xs rounded-full font-medium ${priorityColor} ${task.priority === 'high' ? 'animate-pulse' : ''}`}>
                            {task.priority}
                          </span>
                          <span className={`flex items-center gap-1 text-xs font-medium px-1.5 py-0.5 rounded-full ${
                            isOverdue ? 'bg-red-50 text-red-500' : 'bg-gray-100 text-gray-500'
                          }`}>
                            {isOverdue && <FiAlertCircle size={10} />}
                            {dueDateLabel}
                          </span>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            )}
          </div>

          {/* Leave Requests */}
          <div className="bg-white rounded-lg shadow-card p-6">
            <div className="flex justify-between items-center mb-5">
              <h2 className="text-lg font-medium text-secondary-900">Leave Requests</h2>
              <button
                onClick={() => setShowLeaveForm(true)}
                className="px-3 py-1.5 bg-blue-500 text-white rounded-md hover:bg-blue-600 flex items-center text-sm"
              >
                <FiPlus className="mr-1" size={14} /> Request Leave
              </button>
            </div>
            {showLeaveForm && (
              <div className="mb-4 p-4 border border-gray-200 rounded-lg bg-gray-50">
                <h3 className="text-sm font-medium mb-3">New Leave Request</h3>
                <form onSubmit={handleLeaveSubmit} className="grid grid-cols-1 gap-3">
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Start Date</label>
                    <input type="date" value={leaveForm.start_date} onChange={(e) => setLeaveForm({...leaveForm, start_date: e.target.value})} required className="w-full border border-gray-300 rounded-md px-3 py-1.5 text-sm" />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">End Date</label>
                    <input type="date" value={leaveForm.end_date} onChange={(e) => setLeaveForm({...leaveForm, end_date: e.target.value})} required className="w-full border border-gray-300 rounded-md px-3 py-1.5 text-sm" />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Reason</label>
                    <input type="text" value={leaveForm.reason} onChange={(e) => setLeaveForm({...leaveForm, reason: e.target.value})} required placeholder="Reason for leave" className="w-full border border-gray-300 rounded-md px-3 py-1.5 text-sm" />
                  </div>
                  <div className="flex gap-2">
                    <button type="submit" className="px-3 py-1.5 bg-green-500 text-white rounded-md hover:bg-green-600 text-sm">Submit</button>
                    <button type="button" onClick={() => setShowLeaveForm(false)} className="px-3 py-1.5 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400 text-sm">Cancel</button>
                  </div>
                </form>
              </div>
            )}
            {leaveRequests.length === 0 ? (
              <p className="text-center py-4 text-secondary-500 text-sm">No leave requests found.</p>
            ) : (
              <div className="space-y-2">
                {leaveRequests.map(request => (
                  <div key={request.id} className="flex items-center justify-between px-3 py-2 rounded-lg bg-gray-50">
                    <div>
                      <p className="text-sm text-gray-800">{new Date(request.start_date).toLocaleDateString()} — {new Date(request.end_date).toLocaleDateString()}</p>
                      <p className="text-xs text-gray-400">{request.reason}</p>
                    </div>
                    <span className={`px-2 py-0.5 text-xs rounded-full ${getStatusBadge(request.status)}`}>
                      {request.status.charAt(0).toUpperCase() + request.status.slice(1)}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
      
      {!loading && !error && (
        /* Quick Actions */
        <div className="bg-white rounded-lg shadow-card p-6">
          <h2 className="text-lg font-medium text-secondary-900 mb-4">Quick Actions</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Link to="/employee/tasks" className="p-4 border border-secondary-200 rounded-lg hover:bg-secondary-50">
              <h3 className="font-medium">View My Tasks</h3>
              <p className="text-sm text-secondary-500">See all tasks assigned to you</p>
            </Link>
            <Link to="/employee/tasks" className="p-4 border border-secondary-200 rounded-lg hover:bg-secondary-50">
              <h3 className="font-medium">Update Task Status</h3>
              <p className="text-sm text-secondary-500">Mark tasks as in progress or completed</p>
            </Link>
            <Link to="/time-tracking" className="p-4 border border-secondary-200 rounded-lg hover:bg-secondary-50">
              <h3 className="font-medium">Time Reports</h3>
              <p className="text-sm text-secondary-500">View project & task wise time reports</p>
            </Link>
            <Link to="/employee/monthly-report" className="p-4 border border-secondary-200 rounded-lg hover:bg-secondary-50">
              <h3 className="font-medium">Monthly Report</h3>
              <p className="text-sm text-secondary-500">View detailed monthly time tracking analytics</p>
            </Link>
          </div>
        </div>
      )}
    </div>
  );
};

export default EmployeeDashboard;